import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare,
  Send,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Zap,
  FlaskConical,
  ScrollText,
  Phone,
} from "lucide-react";

export default function TwilioPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState(
    "Halo dari SIMPELKAMI GPDP. Ini adalah pesan test dari sistem."
  );
  const [testChannel, setTestChannel] = useState("SMS");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState(null);
  const [logFilter, setLogFilter] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notifications/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const params =
        logFilter === "ALL" ? "" : `&status=${logFilter}`;
      const { data } = await api.get(`/admin/notifications/logs?limit=25${params}`);
      setLogs(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [logFilter]);

  const send = async () => {
    if (!testTo || !testTo.startsWith("+")) {
      toast.error("Nomor tujuan harus format internasional (contoh +6281234567890)");
      return;
    }
    if (!testBody.trim()) {
      toast.error("Isi pesan tidak boleh kosong");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/notifications/test", {
        to: testTo.trim(),
        body: testBody.trim(),
        channel: testChannel,
      });
      setResult(data);
      const label = data.status === "MOCKED" ? "MOCKED (log saja)" : data.status;
      toast.success(`Test ${testChannel} dikirim → ${label}`);
      load();
      loadLogs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const isMocked = status?.mode === "MOCKED";

  return (
    <Card
      className="overflow-hidden border-primary/20 shadow-lg"
      data-testid="twilio-panel"
    >
      <CardHeader className="bg-gradient-to-r from-emerald-500/8 via-emerald-400/4 to-transparent border-b border-emerald-500/10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-500/10 ring-2 ring-emerald-500/20 flex items-center justify-center shrink-0">
              <MessageSquare className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                Notifikasi SMS & WhatsApp (Twilio)
                {isMocked ? (
                  <Badge
                    variant="outline"
                    className="text-[0.65rem] font-bold border-amber-500/50 text-amber-700 bg-amber-50"
                    data-testid="twilio-mode-badge"
                  >
                    <FlaskConical className="h-2.5 w-2.5 mr-1" /> MOCKED
                  </Badge>
                ) : (
                  <Badge
                    className="text-[0.65rem] font-bold bg-emerald-600"
                    data-testid="twilio-mode-badge"
                  >
                    <Zap className="h-2.5 w-2.5 mr-1" /> LIVE
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1 max-w-xl">
                {isMocked
                  ? "Kredensial Twilio belum diisi. Semua pesan akan dicatat di log tanpa dikirim. Isi TWILIO_* env vars untuk aktifkan."
                  : "Twilio siap. Pesan test akan dikirim ke nomor tujuan sungguhan."}
              </CardDescription>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="btn-twilio-refresh"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* STATUS GRID */}
        {status && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatusPill
              label="Account SID"
              ok={status.account_sid_present}
              testId="twilio-sid"
            />
            <StatusPill
              label="Auth Token"
              ok={status.auth_token_present}
              testId="twilio-token"
            />
            <StatusPill
              label="SMS Sender"
              ok={status.sms_configured}
              hint={status.sms_from || "belum diisi"}
              testId="twilio-sms"
            />
            <StatusPill
              label="WA Sender"
              ok={status.whatsapp_configured}
              hint={status.whatsapp_from || "belum diisi"}
              testId="twilio-wa"
            />
          </div>
        )}

        {/* MOCKED HINT */}
        {isMocked && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold mb-1">Untuk aktifkan pengiriman nyata:</div>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>
                    Daftar Twilio di{" "}
                    <a
                      href="https://www.twilio.com/try-twilio"
                      className="underline font-semibold"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      twilio.com/try-twilio
                    </a>
                    (gratis $15 trial).
                  </li>
                  <li>Copy Account SID, Auth Token, Phone Number dari Console.</li>
                  <li>
                    Isi <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>,{" "}
                    <code>TWILIO_PHONE_NUMBER</code>, dan opsional{" "}
                    <code>TWILIO_WHATSAPP_FROM</code> di Deployment Setting.
                  </li>
                  <li>
                    Untuk WhatsApp, aktifkan Sandbox atau daftarkan Business API di Twilio
                    Console.
                  </li>
                  <li>Redeploy. Panel ini akan otomatis berpindah ke mode LIVE.</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* TEST FORM */}
        <div className="border-t border-border pt-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Kirim Pesan Test
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
            <div>
              <Label className="text-xs">Kanal</Label>
              <Select value={testChannel} onValueChange={setTestChannel}>
                <SelectTrigger data-testid="select-twilio-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SMS">📱 SMS</SelectItem>
                  <SelectItem value="WHATSAPP">💬 WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">
                Nomor Tujuan{" "}
                <span className="text-muted-foreground">
                  (format E.164, contoh +6281234567890)
                </span>
              </Label>
              <Input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="+6281234567890"
                className="font-mono"
                data-testid="input-twilio-to"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Isi Pesan</Label>
            <Textarea
              value={testBody}
              onChange={(e) => setTestBody(e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="input-twilio-body"
            />
            <div className="text-[0.7rem] text-muted-foreground text-right mt-1">
              {testBody.length} karakter
            </div>
          </div>
          <Button
            onClick={send}
            disabled={sending || !testTo || !testBody.trim()}
            className="rounded-full"
            data-testid="btn-twilio-send"
            style={{ backgroundColor: "#3C8C4A" }}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {sending ? "Mengirim…" : `Kirim Test ${testChannel}`}
          </Button>

          {result && (
            <div
              className={`text-xs rounded-md p-3 border ${
                result.status === "SENT"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                  : result.status === "MOCKED"
                  ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-rose-50 border-rose-200 text-rose-900"
              }`}
              data-testid="twilio-test-result"
            >
              <div className="font-bold flex items-center gap-1.5">
                {result.status === "SENT" ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : result.status === "MOCKED" ? (
                  <FlaskConical className="h-3.5 w-3.5" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
                Status: {result.status}
              </div>
              {result.provider_id && (
                <div className="mt-1 font-mono text-[0.7rem]">SID: {result.provider_id}</div>
              )}
              {result.error && (
                <div className="mt-1 font-mono text-[0.7rem]">{result.error}</div>
              )}
              {result.log_id && (
                <div className="mt-1 font-mono text-[0.7rem] text-muted-foreground">
                  log_id: {result.log_id}
                </div>
              )}
            </div>
          )}
        </div>

        {/* LOGS */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <ScrollText className="h-3.5 w-3.5" /> Riwayat Notifikasi
              {logs && (
                <Badge variant="outline" className="text-[0.65rem]">
                  {logs.total} total
                </Badge>
              )}
            </div>
            <Select value={logFilter} onValueChange={setLogFilter}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="select-log-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua</SelectItem>
                <SelectItem value="SENT">Sent</SelectItem>
                <SelectItem value="MOCKED">Mocked</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {logs?.logs?.length ? (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {logs.logs.map((l) => (
                <div
                  key={l.id}
                  className="bg-muted/40 rounded-md p-2 border border-border/60 flex items-start gap-2 text-xs"
                  data-testid={`log-item-${l.id}`}
                >
                  <div className="shrink-0 mt-0.5">
                    {l.status === "SENT" ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    ) : l.status === "MOCKED" ? (
                      <FlaskConical className="h-3.5 w-3.5 text-amber-600" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[0.6rem] font-mono">
                        {l.channel}
                      </Badge>
                      <span className="font-mono text-[0.7rem] flex items-center gap-1">
                        <Phone className="h-2.5 w-2.5" />
                        {l.to}
                      </span>
                      <span
                        className={`text-[0.6rem] font-bold uppercase ${
                          l.status === "SENT"
                            ? "text-emerald-700"
                            : l.status === "MOCKED"
                            ? "text-amber-700"
                            : "text-rose-700"
                        }`}
                      >
                        {l.status}
                      </span>
                    </div>
                    <div className="text-muted-foreground truncate mt-0.5">{l.body}</div>
                    <div className="text-[0.6rem] text-muted-foreground/70 mt-0.5">
                      {l.timestamp?.slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic py-3 text-center">
              Belum ada riwayat notifikasi.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ label, ok, hint, testId }) {
  return (
    <div
      className={`rounded-lg p-3 border ${
        ok ? "bg-emerald-50 border-emerald-200" : "bg-muted/40 border-border/60"
      }`}
      data-testid={testId}
    >
      <div className="text-[0.65rem] uppercase tracking-widest font-bold text-muted-foreground">
        {label}
      </div>
      <div
        className={`text-xs font-bold mt-1 flex items-center gap-1 ${
          ok ? "text-emerald-700" : "text-muted-foreground"
        }`}
      >
        {ok ? (
          <>
            <CheckCircle2 className="h-3 w-3" /> Terkonfig
          </>
        ) : (
          <>
            <AlertTriangle className="h-3 w-3" /> Belum
          </>
        )}
      </div>
      {hint && (
        <div className="text-[0.65rem] font-mono text-muted-foreground mt-1 truncate" title={hint}>
          {hint}
        </div>
      )}
    </div>
  );
}
